<template>
  <div class="card p-4 mb-25 border-0 radius-7 bg-white letter-spacing plan-locked-wrapper" :class="{ 'plan-locked-wrapper--whatsapp': disabledByPlan }">
    <PlanFeatureLocked v-if="disabledByPlan" overlay title="WhatsApp bloqueado pelo plano" message="O plano atual não inclui atendimento automático." />

    <div class="content" :class="{ 'opacity-50': disabledByPlan }">
      <div class="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
        <div>
          <h4 class="mb-1">Canal do WhatsApp</h4>
          <p class="text-muted mb-0">Escolha um único provedor ativo. Não existe fallback entre os canais.</p>
        </div>
        <span class="badge" :class="provider === 'cloud_api' ? 'bg-primary' : 'bg-success'">
          {{ providerBadge }}
        </span>
      </div>

      <div class="provider-grid mb-4">
        <button type="button" class="provider-card" :class="{ active: selectedProvider === 'whatsapp_web_js' }" @click="selectedProvider = 'whatsapp_web_js'">
          <i class="ri-qr-code-line"></i>
          <strong>WhatsApp Web</strong>
          <small>Conexão atual por QR Code</small>
        </button>
        <button type="button" class="provider-card" :class="{ active: selectedProvider === 'cloud_api' }" @click="selectedProvider = 'cloud_api'">
          <i class="ri-whatsapp-line"></i>
          <strong>API Oficial</strong>
          <small>WhatsApp Business Cloud API</small>
        </button>
      </div>

      <div v-if="selectedProvider === 'whatsapp_web_js'">
        <h5>Status da conexão por QR Code</h5>
        <div v-if="getQrCode && !['DISCONNECTED','NOT_FOUND','LOADING','ERROR'].includes(getQrCode)" class="text-center">
          <div class="alert alert-warning">Escaneie o QR Code para conectar.</div>
          <canvas ref="currentQrCode"></canvas>
        </div>
        <div v-else-if="getQrCode === 'LOADING'" class="alert alert-info">Carregando informações...</div>
        <div v-else-if="getQrCode === 'ERROR'" class="alert alert-danger">Erro ao carregar a conexão.</div>
        <div v-else-if="getQrCode === 'DISCONNECTED' || getQrCode === 'NOT_FOUND'" class="alert alert-danger">WhatsApp desconectado.</div>
        <div v-else class="alert alert-success">WhatsApp conectado.</div>

        <label class="form-label">Número do chatbot</label>
        <input v-model="whatsappNumber" class="form-control" :disabled="!editingLegacy" placeholder="(00) 00000-0000">
        <div class="d-flex gap-2 mt-3">
          <button v-if="!editingLegacy" type="button" class="btn btn-outline-primary" @click="editingLegacy = true">Editar número</button>
          <template v-else>
            <button type="button" class="btn btn-secondary" @click="editingLegacy = false">Cancelar</button>
            <button type="button" class="btn btn-success" :disabled="busy" @click="saveLegacy">Salvar</button>
          </template>
          <button v-if="provider !== 'whatsapp_web_js'" type="button" class="btn btn-primary ms-auto" :disabled="busy" @click="activate('whatsapp_web_js')">
            Ativar WhatsApp Web
          </button>
        </div>
      </div>

      <div v-else>
        <div class="official-mode-grid mb-4">
          <button type="button" class="provider-card" :class="{ active: selectedCloudMode === 'coexistence' }" @click="selectedCloudMode = 'coexistence'">
            <i class="ri-smartphone-line"></i>
            <strong>WhatsApp Business + API</strong>
            <small>Mesmo número no celular e no sistema</small>
          </button>
          <button type="button" class="provider-card" :class="{ active: selectedCloudMode === 'manual' }" @click="selectedCloudMode = 'manual'">
            <i class="ri-settings-3-line"></i>
            <strong>Configuração manual</strong>
            <small>Número usado exclusivamente pela Cloud API</small>
          </button>
        </div>

        <section v-if="selectedCloudMode === 'coexistence'">
          <div class="alert alert-info">
            Este modo conecta um número que já está no aplicativo WhatsApp Business. Não exclua a conta do celular e não registre o número manualmente no Gerenciador do WhatsApp.
          </div>
          <div v-if="!isSecureContextForMeta" class="alert alert-warning">
            A janela da Meta exige que esta página seja aberta por HTTPS. Use a URL HTTPS do seu túnel apontando para o frontend antes de clicar em conectar.
          </div>
          <div v-if="!embeddedSignup.enabled" class="alert alert-warning">
            O Embedded Signup ainda não foi configurado no backend. Preencha App ID, App Secret, Configuration ID e Verify Token nas variáveis de ambiente.
          </div>

          <div class="alert" :class="coexistenceAlertClass">
            <strong>{{ coexistenceStatusLabel }}</strong>
            <span v-if="coexistence.last_error"> — {{ coexistence.last_error }}</span>
          </div>

          <div v-if="coexistence.sync_warning" class="alert alert-warning">
            <strong>A conexão está ativa para novas mensagens.</strong>
            A sincronização do histórico ou dos contatos não foi concluída. O suporte pode verificar essa etapa sem refazer a conexão.
            <small class="d-block mt-1">{{ coexistence.sync_warning }}</small>
          </div>

          <div v-if="coexistence.configured" class="row g-3 mb-3">
            <div class="col-md-6">
              <label class="form-label">Número conectado</label>
              <input :value="coexistence.display_phone_number || 'Aguardando confirmação'" readonly class="form-control">
            </div>
            <div class="col-md-6">
              <label class="form-label">WABA ID</label>
              <input :value="coexistence.waba_id || 'Aguardando confirmação'" readonly class="form-control">
            </div>
          </div>

          <div class="webhook-box">
            <label class="form-label">Webhook global da coexistência</label>
            <div class="input-group">
              <input :value="embeddedSignup.webhook_url" readonly class="form-control">
              <button type="button" class="btn btn-outline-secondary" @click="copy(embeddedSignup.webhook_url)">Copiar</button>
            </div>
            <small class="text-muted d-block mt-2">
              No painel da Meta, assine os campos messages, smb_message_echoes, history e smb_app_state_sync.
            </small>
          </div>

          <div v-if="phoneCandidates.length" class="phone-selector mt-3">
            <label class="form-label">Escolha o número autorizado pela Meta</label>
            <select v-model="selectedPhoneId" class="form-select">
              <option value="">Selecione</option>
              <option v-for="phone in phoneCandidates" :key="phone.phone_number_id" :value="phone.phone_number_id">
                {{ phone.display_phone_number || phone.phone_number_id }} — {{ phone.verified_name || 'WhatsApp Business' }}
              </option>
            </select>
            <button type="button" class="btn btn-primary mt-2" :disabled="busy || !selectedPhoneId" @click="confirmPhoneSelection">
              Confirmar número
            </button>
          </div>

          <div class="d-flex flex-wrap gap-2 mt-3">
            <button type="button" class="btn btn-outline-primary" :disabled="busy" @click="checkCoexistence()">
              {{ checkingConnection ? 'Verificando na Meta...' : 'Verificar conexão na Meta' }}
            </button>
            <button type="button" class="btn btn-success" :disabled="busy || !embeddedSignup.enabled || !isSecureContextForMeta" @click="connectCoexistence">
              {{ coexistence.configured ? 'Reconectar WhatsApp Business' : 'Conectar WhatsApp Business' }}
            </button>
            <button
              v-if="provider !== 'cloud_api' || activeCloudMode !== 'coexistence'"
              type="button"
              class="btn btn-primary ms-auto"
              :disabled="busy || coexistence.status !== 'active'"
              @click="activate('cloud_api', 'coexistence')"
            >
              Ativar coexistência
            </button>
          </div>

          <p v-if="coexistence.status !== 'active'" class="small text-muted mt-2 mb-0">
            Já concluiu a conexão na Meta e no celular? Clique em Verificar conexão na Meta para consultar a autorização salva e retomar a finalização.
          </p>

          <p v-if="provider === 'cloud_api' && activeCloudMode === 'coexistence'" class="small text-muted mt-2 mb-0">
            A coexistência já está selecionada no sistema. Após reconectar, não é necessário ativar esse modo novamente.
          </p>

          <div class="alert alert-light border mt-3 mb-0">
            Respostas livres pela API funcionam dentro da janela de 24 horas iniciada pelo cliente. Fora dela, use um template aprovado ou continue pelo aplicativo no celular. Campanhas e templates são gerenciados em Campanhas WhatsApp.
          </div>
        </section>

        <section v-else>
          <div class="alert" :class="manualCloud.status === 'active' ? 'alert-success' : (manualCloud.status === 'invalid' ? 'alert-danger' : 'alert-warning')">
            <strong>{{ manualCloudStatusLabel }}</strong>
            <span v-if="manualCloud.last_error"> — {{ manualCloud.last_error }}</span>
          </div>
          <div class="row g-3">
            <div class="col-md-6"><label class="form-label">WABA ID</label><input v-model="form.waba_id" class="form-control" autocomplete="off"></div>
            <div class="col-md-6"><label class="form-label">App ID (templates com mídia)</label><input v-model="form.meta_app_id" class="form-control" autocomplete="off" placeholder="ID do aplicativo que emitiu o token"></div>
            <div class="col-md-6"><label class="form-label">Phone Number ID</label><input v-model="form.phone_number_id" class="form-control" autocomplete="off"></div>
            <div class="col-md-6"><label class="form-label">Número exibido</label><input v-model="form.display_phone_number" class="form-control" placeholder="5511999999999"></div>
            <div class="col-md-6"><label class="form-label">Token permanente</label><input v-model="form.access_token" type="password" class="form-control" :placeholder="manualCloud.token_masked || 'Informe o token'"></div>
            <div class="col-md-6"><label class="form-label">App Secret</label><input v-model="form.app_secret" type="password" class="form-control" :placeholder="manualCloud.app_secret_configured ? 'Já configurado — deixe vazio para manter' : 'Informe o App Secret'"></div>
          </div>
          <div v-if="manualCloud.configured" class="webhook-box mt-3">
            <label class="form-label">URL do webhook</label>
            <div class="input-group">
              <input :value="manualCloud.webhook_url" readonly class="form-control">
              <button type="button" class="btn btn-outline-secondary" @click="copy(manualCloud.webhook_url)">Copiar</button>
            </div>
            <label class="form-label mt-2">Verify Token</label>
            <div class="input-group">
              <input :value="manualCloud.verify_token" readonly class="form-control">
              <button type="button" class="btn btn-outline-secondary" @click="copy(manualCloud.verify_token)">Copiar</button>
            </div>
          </div>
          <div class="d-flex flex-wrap gap-2 mt-3">
            <button type="button" class="btn btn-outline-primary" :disabled="busy" @click="saveCloud">Salvar credenciais</button>
            <button type="button" class="btn btn-success" :disabled="busy || !manualCloud.configured" @click="validateCloud">Validar na Meta</button>
            <button
              v-if="provider !== 'cloud_api' || activeCloudMode !== 'manual'"
              type="button"
              class="btn btn-primary ms-auto"
              :disabled="busy || manualCloud.status !== 'active'"
              @click="activate('cloud_api', 'manual')"
            >
              Ativar API Oficial manual
            </button>
          </div>

        </section>
      </div>

      <div v-if="provider === 'cloud_api' && getAllowedPages.includes('whatsapp-campaigns')" class="alert alert-info mt-3">Templates e envios em massa agora ficam em <router-link to="/whatsapp-campaigns">Campanhas WhatsApp</router-link>.</div>
      <div v-if="feedback" class="alert mt-3" :class="feedbackError ? 'alert-danger' : 'alert-success'">{{ feedback }}</div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import QRCode from 'qrcode';
import PlanFeatureLocked from '../Common/PlanFeatureLocked.vue';

export default defineComponent({
  name: 'WhatsappConfigurations',
  components: { PlanFeatureLocked },
  props: { disabledByPlan: { type: Boolean, default: false } },
  data: () => ({
    selectedProvider: 'whatsapp_web_js',
    selectedCloudMode: 'coexistence',
    settingsInitialized: false,
    whatsappNumber: '',
    editingLegacy: false,
    busy: false,
    feedback: '',
    feedbackError: false,
    pendingState: '',
    pendingCode: '',
    pendingMetaSession: null,
    completingSignup: false,
    signupCompleted: false,
    signupCancelled: false,
    signupAttempt: 0,
    signupFallbackTimeout: null,
    checkingConnection: false,
    phoneSelectionSource: 'signup',
    phoneCandidates: [],
    selectedPhoneId: '',
    signupTimeout: null,
    form: { meta_app_id: '', waba_id: '', phone_number_id: '', display_phone_number: '', access_token: '', app_secret: '' },
  }),
  computed: {
    ...mapGetters(['getQrCode', 'getWhatsappNumber', 'getWhatsappSettings', 'getAllowedPages']),
    provider() { return this.getWhatsappSettings?.provider || 'whatsapp_web_js'; },
    activeCloudMode() { return this.getWhatsappSettings?.cloud_mode || 'manual'; },
    manualCloud() { return this.getWhatsappSettings?.cloud_manual || { configured: false, status: 'draft' }; },
    coexistence() { return this.getWhatsappSettings?.coexistence || { configured: false, status: 'draft' }; },
    embeddedSignup() { return this.getWhatsappSettings?.embedded_signup || { enabled: false, webhook_url: '' }; },
    providerBadge() {
      if (this.provider !== 'cloud_api') return 'WhatsApp Web ativo';
      return this.activeCloudMode === 'coexistence' ? 'API Oficial + celular ativos' : 'API Oficial manual ativa';
    },
    manualCloudStatusLabel() {
      return { active: 'Configuração validada', invalid: 'Configuração inválida', draft: 'Configuração ainda não validada' }[this.manualCloud.status] || 'Não configurado';
    },
    coexistenceStatusLabel() {
      return {
        active: 'WhatsApp Business conectado em coexistência',
        onboarding: 'Finalizando a conexão com a Meta',
        disconnected: 'A coexistência foi desconectada no celular',
        invalid: 'A Meta não confirmou a coexistência',
        draft: 'WhatsApp Business ainda não conectado',
      }[this.coexistence.status] || 'WhatsApp Business ainda não conectado';
    },
    coexistenceAlertClass() {
      if (this.coexistence.status === 'active') return 'alert-success';
      if (['invalid', 'disconnected'].includes(this.coexistence.status)) return 'alert-danger';
      return 'alert-warning';
    },
    isSecureContextForMeta() {
      return window.location.protocol === 'https:' || ['localhost', '127.0.0.1'].includes(window.location.hostname);
    },
  },
  watch: {
    getWhatsappNumber(value) { this.whatsappNumber = this.mask(value); },
    getQrCode(value) {
      if (value && !['DISCONNECTED', 'NOT_FOUND', 'LOADING', 'ERROR'].includes(value)) {
        this.$nextTick(() => {
          if (this.$refs.currentQrCode) QRCode.toCanvas(this.$refs.currentQrCode, value);
        });
      }
    },
    getWhatsappSettings: {
      deep: true,
      handler(settings) {
        if (!settings) return;
        if (!this.settingsInitialized) {
          const legacyConnected = settings.provider === 'whatsapp_web_js' && settings.legacy?.status === true;
          this.selectedProvider = legacyConnected ? 'whatsapp_web_js' : 'cloud_api';
          this.selectedCloudMode = settings.cloud_mode === 'coexistence' || !settings.cloud_manual?.configured
            ? 'coexistence'
            : 'manual';
          this.settingsInitialized = true;
        }
        const cloud = settings.cloud_manual || {};
        this.form = {
          waba_id: cloud.waba_id || '',
          meta_app_id: cloud.meta_app_id || '',
          phone_number_id: cloud.phone_number_id || '',
          display_phone_number: cloud.display_phone_number || '',
          access_token: '',
          app_secret: '',
        };
      },
    },
  },
  methods: {
    ...mapActions([
      'saveWhatsappNumber', 'fetchWhatsappSettings', 'selectWhatsappProvider',
      'saveWhatsappCloudCredentials', 'validateWhatsappCloud',
      'startWhatsappCoexistenceSession', 'completeWhatsappCoexistenceSession',
      'selectWhatsappCoexistencePhone',
      'checkWhatsappCoexistence',
      'recordWhatsappCoexistenceDiagnostic',
    ]),
    mask(value) {
      const digits = String(value || '').replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');
      return digits.length > 6 ? `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}` : digits;
    },
    async run(action, success) {
      this.busy = true;
      this.feedback = '';
      try {
        await action();
        this.feedback = success;
        this.feedbackError = false;
      } catch (error) {
        this.feedback = error.response?.data?.message || error.response?.data?.details || 'Não foi possível concluir a operação.';
        this.feedbackError = true;
      } finally {
        this.busy = false;
      }
    },
    saveLegacy() {
      return this.run(async () => {
        await this.saveWhatsappNumber({ number: this.whatsappNumber.replace(/\D/g, '') });
        this.editingLegacy = false;
      }, 'Número salvo.');
    },
    saveCloud() { return this.run(() => this.saveWhatsappCloudCredentials({ ...this.form }), 'Credenciais salvas com segurança.'); },
    validateCloud() { return this.run(() => this.validateWhatsappCloud(), 'Conta validada e webhook assinado no WABA.'); },
    activate(provider, cloudMode = null) {
      return this.run(
        () => this.selectWhatsappProvider(cloudMode ? { provider, cloud_mode: cloudMode } : { provider }),
        'Provedor ativo atualizado.',
      );
    },
    copy(value) {
      navigator.clipboard?.writeText(value || '');
      this.feedback = 'Copiado.';
      this.feedbackError = false;
    },
    handleMetaMessage(event) {
      if (!this.pendingState || this.signupCompleted || this.signupCancelled) return;
      if (!['https://www.facebook.com', 'https://web.facebook.com'].includes(event.origin)) return;
      let payload = event.data;
      try {
        if (typeof payload === 'string') payload = JSON.parse(payload);
      } catch {
        return;
      }
      if (payload?.type !== 'WA_EMBEDDED_SIGNUP') return;
      if (['FINISH', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'].includes(payload.event)) {
        this.pendingMetaSession = payload;
        this.reportSignupDiagnostic('finish_received');
        this.tryCompleteCoexistence();
      } else if (['CANCEL', 'ERROR'].includes(payload.event) && !this.completingSignup) {
        this.signupCancelled = true;
        clearTimeout(this.signupTimeout);
        clearTimeout(this.signupFallbackTimeout);
        this.reportSignupDiagnostic(payload.event === 'CANCEL' ? 'cancel_received' : 'error_received');
        this.busy = false;
        this.feedback = payload.event === 'CANCEL'
          ? 'A janela da Meta foi encerrada antes de concluir a autorização.'
          : 'A Meta informou uma falha na conexão. O suporte pode consultar o registro desta tentativa.';
        this.feedbackError = true;
      }
    },
    reportSignupDiagnostic(stage) {
      if (!this.pendingState) return;
      this.recordWhatsappCoexistenceDiagnostic({
        state: this.pendingState,
        stage,
        has_code: Boolean(this.pendingCode),
        has_finish: Boolean(this.pendingMetaSession),
      }).catch(() => undefined);
    },
    loadFacebookSdk(config) {
      return new Promise((resolve, reject) => {
        const initialize = () => {
          if (!window.FB) {
            reject(new Error('O SDK da Meta não foi carregado.'));
            return;
          }
          window.FB.init({
            appId: config.app_id,
            cookie: true,
            xfbml: false,
            version: config.sdk_version || 'v25.0',
          });
          resolve();
        };
        if (window.FB) {
          initialize();
          return;
        }
        window.fbAsyncInit = initialize;
        const existing = document.getElementById('facebook-jssdk');
        if (existing) {
          existing.addEventListener('load', initialize, { once: true });
          existing.addEventListener('error', () => reject(new Error('Não foi possível carregar a janela da Meta.')), { once: true });
          return;
        }
        const script = document.createElement('script');
        script.id = 'facebook-jssdk';
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
        script.src = 'https://connect.facebook.net/pt_BR/sdk.js';
        script.onerror = () => reject(new Error('Não foi possível carregar a janela da Meta.'));
        document.head.appendChild(script);
      });
    },
    async connectCoexistence() {
      const attempt = ++this.signupAttempt;
      clearTimeout(this.signupTimeout);
      clearTimeout(this.signupFallbackTimeout);
      this.busy = true;
      this.feedback = '';
      this.feedbackError = false;
      this.phoneCandidates = [];
      this.phoneSelectionSource = 'signup';
      this.selectedPhoneId = '';
      this.pendingCode = '';
      this.pendingMetaSession = null;
      this.pendingState = '';
      this.signupCompleted = false;
      this.signupCancelled = false;
      try {
        const session = await this.startWhatsappCoexistenceSession();
        this.pendingState = session.state;
        await this.loadFacebookSdk(session);
        this.reportSignupDiagnostic('sdk_loaded');
        // This follows the backend session lifetime, rather than failing while
        // the customer is still completing the steps on their phone.
        this.signupTimeout = setTimeout(() => {
          if (attempt !== this.signupAttempt || this.signupCompleted || this.signupCancelled || this.completingSignup) return;
          this.reportSignupDiagnostic('timeout');
          this.busy = false;
          this.feedback = this.pendingMetaSession
            ? 'A Meta confirmou a conclusão, mas não devolveu o código de autorização ao sistema. O suporte pode consultar esta tentativa.'
            : 'O retorno da autorização da Meta não chegou ao sistema no prazo desta sessão. O suporte pode consultar esta tentativa.';
          this.feedbackError = true;
        }, Math.max(1000, Math.min(600000, Date.parse(session.expires_at) - Date.now() || 600000)));
        this.reportSignupDiagnostic('login_requested');
        window.FB.login((response) => {
          if (attempt !== this.signupAttempt || this.signupCompleted || this.signupCancelled) return;
          const code = response?.authResponse?.code;
          if (!code) {
            clearTimeout(this.signupTimeout);
            this.reportSignupDiagnostic('auth_code_missing');
            this.busy = false;
            this.feedback = 'A Meta não devolveu o código de autorização ao sistema. O suporte pode consultar o registro desta tentativa.';
            this.feedbackError = true;
            return;
          }
          this.pendingCode = code;
          this.reportSignupDiagnostic('auth_code_received');
          this.tryCompleteCoexistence();
          if (!this.completingSignup && !this.signupCompleted) {
            this.signupFallbackTimeout = setTimeout(() => {
              if (attempt === this.signupAttempt) this.tryCompleteCoexistence(true);
            }, 1500);
          }
        }, {
          config_id: session.configuration_id,
          response_type: 'code',
          override_default_response_type: true,
          extras: {
            setup: {},
            featureType: 'whatsapp_business_app_onboarding',
            sessionInfoVersion: '3',
          },
        });
      } catch (error) {
        clearTimeout(this.signupTimeout);
        clearTimeout(this.signupFallbackTimeout);
        this.reportSignupDiagnostic('launch_failed');
        this.busy = false;
        this.feedback = error.response?.data?.message || error.message || 'Não foi possível abrir o Embedded Signup.';
        this.feedbackError = true;
      }
    },
    async tryCompleteCoexistence(allowMissingSession = false) {
      if (!this.pendingState || !this.pendingCode || (!this.pendingMetaSession && !allowMissingSession) || this.completingSignup || this.signupCompleted || this.signupCancelled) return;
      this.completingSignup = true;
      this.busy = true;
      clearTimeout(this.signupTimeout);
      clearTimeout(this.signupFallbackTimeout);
      this.reportSignupDiagnostic('completion_requested');
      try {
        await this.completeWhatsappCoexistenceSession({
          state: this.pendingState,
          code: this.pendingCode,
          ...(this.pendingMetaSession ? { session: this.pendingMetaSession } : {}),
        });
        this.feedback = 'Conexão recebida. A validação final está sendo processada.';
        this.feedbackError = false;
        await this.refreshCoexistenceStatus();
        if (['invalid', 'disconnected'].includes(this.coexistence.status)) {
          this.feedback = this.coexistence.last_error || 'A Meta não confirmou a coexistência deste número.';
          this.feedbackError = true;
        } else if (this.coexistence.status === 'active') {
          this.feedback = this.provider === 'cloud_api' && this.activeCloudMode === 'coexistence'
            ? 'Conexão confirmada. A coexistência já está ativa no sistema.'
            : 'Conexão confirmada. Agora clique em Ativar coexistência.';
        }
      } catch (error) {
        if (error.response?.status === 409 && error.response?.data?.code === 'COEXISTENCE_PHONE_SELECTION_REQUIRED') {
          this.phoneCandidates = error.response.data.phones || [];
          this.pendingState = error.response.data.state || this.pendingState;
          this.feedback = error.response.data.message;
          this.feedbackError = false;
        } else {
          this.reportSignupDiagnostic('completion_failed');
          this.feedback = error.response?.data?.message || 'A Meta não confirmou a conexão.';
          this.feedbackError = true;
        }
      } finally {
        // Authorization codes are single-use; delayed Meta events must not
        // send the same code a second time after a response or network error.
        this.signupCompleted = true;
        this.pendingCode = '';
        this.busy = false;
        this.completingSignup = false;
      }
    },
    async confirmPhoneSelection() {
      if (this.phoneSelectionSource === 'check') {
        return this.checkCoexistence(this.selectedPhoneId);
      }
      await this.run(async () => {
        await this.selectWhatsappCoexistencePhone({
          state: this.pendingState,
          phone_number_id: this.selectedPhoneId,
        });
        this.phoneCandidates = [];
        this.selectedPhoneId = '';
        await this.refreshCoexistenceStatus();
      }, 'Número selecionado. A validação final está sendo processada.');
    },
    async checkCoexistence(phoneNumberId = '') {
      this.busy = true;
      this.checkingConnection = true;
      this.feedback = '';
      this.feedbackError = false;
      try {
        const result = await this.checkWhatsappCoexistence(phoneNumberId ? { phone_number_id: phoneNumberId } : {});
        this.phoneCandidates = [];
        this.selectedPhoneId = '';
        await this.refreshCoexistenceStatus();
        if (this.coexistence.status === 'active') {
          this.feedback = this.provider === 'cloud_api' && this.activeCloudMode === 'coexistence'
            ? 'Conexão confirmada na Meta. A coexistência já está ativa no sistema.'
            : 'Conexão confirmada na Meta. Agora clique em Ativar coexistência.';
        } else if (['invalid', 'disconnected'].includes(this.coexistence.status)) {
          this.feedback = this.coexistence.last_error || 'A Meta não confirmou a coexistência deste número.';
          this.feedbackError = true;
        } else {
          this.feedback = result.message || 'A finalização continua em processamento. Verifique novamente em instantes.';
        }
      } catch (error) {
        if (error.response?.status === 409 && error.response?.data?.code === 'COEXISTENCE_PHONE_SELECTION_REQUIRED') {
          this.phoneCandidates = error.response.data.phones || [];
          this.selectedPhoneId = '';
          this.phoneSelectionSource = 'check';
          this.feedback = error.response.data.message;
        } else {
          this.feedback = error.response?.data?.message || 'Não foi possível consultar a Meta agora. Tente novamente em instantes.';
          this.feedbackError = true;
        }
      } finally {
        this.busy = false;
        this.checkingConnection = false;
      }
    },
    async refreshCoexistenceStatus() {
      for (let attempt = 0; attempt < 5; attempt += 1) {
        await this.fetchWhatsappSettings();
        if (['active', 'invalid'].includes(this.coexistence.status)) break;
        await new Promise(resolve => setTimeout(resolve, 1200));
      }
    },
  },
  async mounted() {
    window.addEventListener('message', this.handleMetaMessage);
    await this.fetchWhatsappSettings().catch(error => console.warn('Não foi possível carregar a integração', error));
  },
  unmounted() {
    window.removeEventListener('message', this.handleMetaMessage);
    clearTimeout(this.signupTimeout);
    clearTimeout(this.signupFallbackTimeout);
    this.signupAttempt += 1;
  },
});
</script>

<style scoped>
.plan-locked-wrapper{overflow:hidden;position:relative}
.provider-grid,.official-mode-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
.provider-card{display:flex;flex-direction:column;align-items:flex-start;gap:4px;padding:18px;border:1px solid #dbe2ea;border-radius:10px;background:#fff;color:#334155;text-align:left}
.provider-card i{font-size:26px}
.provider-card.active{border-color:#4f46e5;box-shadow:0 0 0 2px rgba(79,70,229,.12);color:#312e81}
.provider-card small{color:#64748b}
.webhook-box,.template-form,.phone-selector{padding:16px;border-radius:9px;background:#f8fafc;border:1px solid #e2e8f0}
@media(max-width:767px){.provider-grid,.official-mode-grid{grid-template-columns:1fr}}
</style>
